import { useState, type FormEvent } from 'react';
import { parseCsv, tidioContacts, tidioKnowledge, tidioTranscripts } from '@prakash-software/shared';
import { importErrorMessage, importTidio, type SkippedRow, type TidioImportKind } from '../../lib/tidio-import-api';
import styles from './admin.module.css';

interface Preview {
  /** "120 contacts", or why the file can't be used. */
  summary: string;
  ok: boolean;
  skipped: number;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function previewOf(kind: TidioImportKind, texts: string[]): Preview {
  if (kind === 'contacts') {
    const p = tidioContacts(parseCsv(texts[0] ?? ''));
    if (p.missingHeader) return { summary: 'The file needs an “Email” or “Phone” column.', ok: false, skipped: 0 };
    return { summary: plural(p.items.length, 'contact'), ok: p.items.length > 0, skipped: p.skipped.length };
  }
  if (kind === 'conversations') {
    let convs = 0;
    let msgs = 0;
    let skipped = 0;
    for (const t of texts) {
      const p = tidioTranscripts(parseCsv(t));
      if (p.missingHeader) return { summary: 'Each file needs a “Message” column.', ok: false, skipped: 0 };
      convs += p.items.length;
      msgs += p.items.reduce((n, c) => n + c.messages.length, 0);
      skipped += p.skipped.length;
    }
    return { summary: `${plural(convs, 'conversation')} (${plural(msgs, 'message')})`, ok: convs > 0, skipped };
  }
  const p = tidioKnowledge(parseCsv(texts[0] ?? ''));
  return {
    summary: `${plural(p.items.length, 'question and answer', 'questions and answers')}, ${plural(p.urls.length, 'website')}`,
    ok: p.items.length + p.urls.length > 0,
    skipped: p.skipped.length,
  };
}

const skippedNote = (rows: SkippedRow[]) => (rows.length ? `; ${plural(rows.length, 'row')} skipped` : '');

/** Upload, preview and import one kind of Tidio export file. */
function ImportCard({
  siteId,
  kind,
  title,
  hint,
}: {
  siteId: string;
  kind: TidioImportKind;
  title: string;
  hint: string;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped after an import to clear the file input.
  const [inputKey, setInputKey] = useState(0);

  async function pick(list: FileList | null) {
    const picked = list ? [...list] : [];
    setFiles(picked);
    setNotice(null);
    setError(null);
    setPreview(picked.length ? previewOf(kind, await Promise.all(picked.map((f) => f.text()))) : null);
  }

  async function run(e: FormEvent) {
    e.preventDefault();
    if (!files.length || busy || !preview?.ok) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (kind === 'contacts') {
        const r = await importTidio(siteId, 'contacts', files[0]!);
        setNotice(`Imported ${plural(r.created, 'new contact')}, updated ${r.matched} already here${skippedNote(r.skipped)}.`);
      } else if (kind === 'conversations') {
        let imported = 0;
        let duplicates = 0;
        let messages = 0;
        const skipped: SkippedRow[] = [];
        for (const [i, f] of files.entries()) {
          setNotice(`Importing file ${i + 1} of ${files.length}…`);
          const r = await importTidio(siteId, 'conversations', f);
          imported += r.imported;
          duplicates += r.duplicates;
          messages += r.messages;
          skipped.push(...r.skipped);
        }
        setNotice(
          `Imported ${plural(imported, 'conversation')} (${plural(messages, 'message')})` +
            (duplicates ? `; ${duplicates} were already imported` : '') +
            `${skippedNote(skipped)}. They are closed and tagged “tidio-import” in the inbox.`,
        );
      } else {
        const r = await importTidio(siteId, 'knowledge', files[0]!);
        const rejected = r.rejectedWebsites.length ? `; ${plural(r.rejectedWebsites.length, 'website')} refused` : '';
        setNotice(
          `Imported ${plural(r.questions, 'question and answer', 'questions and answers')} and ${plural(r.websites, 'website')} (reading in the background)` +
            (r.duplicateQuestions + r.existingWebsites ? `; ${r.duplicateQuestions + r.existingWebsites} were already here` : '') +
            `${rejected}${skippedNote(r.skipped)}.`,
        );
      }
      setFiles([]);
      setPreview(null);
      setInputKey((k) => k + 1);
    } catch (err) {
      setNotice(null);
      setError(importErrorMessage(err) ?? 'The import failed. Check the file and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={run} style={{ marginBottom: 'var(--inic-space-5)' }}>
      <h3 className={styles.label} style={{ margin: 0 }}>{title}</h3>
      <p className={styles.hint} style={{ marginTop: 0 }}>{hint}</p>
      <label className={styles.field}>
        <span className={styles.label}>CSV {kind === 'conversations' ? 'files' : 'file'}</span>
        <input
          key={inputKey}
          className={styles.input}
          type="file"
          accept=".csv,text/csv,.tsv,.txt"
          multiple={kind === 'conversations'}
          onChange={(e) => void pick(e.target.files)}
        />
      </label>
      {preview && (
        <p className={preview.ok ? styles.hint : styles.error} role="status">
          {preview.ok ? `${preview.summary} ready to import` : preview.summary}
          {preview.ok && preview.skipped > 0 && `; ${plural(preview.skipped, 'row')} will be skipped`}
        </p>
      )}
      {notice && <p className={styles.hint} role="status">{notice}</p>}
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.formActions}>
        <button type="submit" className={styles.button} disabled={busy || !preview?.ok}>
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
    </form>
  );
}

// Moving over from Tidio: bring in contacts, past conversations and the Lyro
// knowledge base from Tidio's export files. Every import can be run again —
// only what is new is added.
export function TidioImportSection({ siteId }: { siteId: string }) {
  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Migration</p>
      <h2 className={styles.title}>Import from Tidio</h2>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        Export your data from Tidio, then upload the files here. Importing the same file again only adds what is new,
        so you can repeat an import with a newer export.
      </p>
      <ImportCard
        siteId={siteId}
        kind="contacts"
        title="Contacts"
        hint="In Tidio: Contacts → select all → Export. Each contact needs an email or phone number; someone already here with the same email or phone is updated, not duplicated. Custom properties become contact attributes."
      />
      <ImportCard
        siteId={siteId}
        kind="conversations"
        title="Conversation history"
        hint="In Tidio: open a conversation → ⋯ in the visitor details → Export transcript to .CSV. Select several files at once. Conversations arrive closed and are linked to the contact with the same email or phone."
      />
      <ImportCard
        siteId={siteId}
        kind="knowledge"
        title="Knowledge base"
        hint="Lyro questions and answers as a CSV with “Question” and “Answer” columns. A “URL” column, or a file listing website addresses, adds those sites as data sources to read."
      />
    </section>
  );
}
